import type { Context as EdgeFunctionsContext, Config } from '@netlify/edge-functions';
import type { Context as FunctionsContext } from '@netlify/functions';
import { COMPONENT_A_B_TEST_VARIANTS_PATH } from './lib/constants.ts'; // Please do not remove .ts file extension. We need it for edge function to work.
import { type output, z } from 'zod';

export const MARKETING_OPT_OUT_FIELD_ID = 'marketing_opt_out';

// SOURCE: https://sonarsource.atlassian.net/browse/SONARSITE-2336
const GDPRCompliantCountries = [
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'CY', // Cyprus
  'CZ', // Czech Republic
  'DK', // Denmark
  'DE', // Germany
  'GR', // Greece
  'IT', // Italy
  'LT', // Lithuania
  'MT', // Malta
  'NL', // Netherlands
  'PT', // Portugal
  'RO', // Romania
  'SK', // Slovakia
  'ES', // Spain
];

const COOKIE_LEADSOURCE_ID = 'LeadSourceNewCookie';

const leadSourceCookieSchema = z
  .object({
    lead_campaign_tag: z.string(),
    lead_gclid: z.string(),
    lead_content: z.string(),
    lead_term: z.string(),
    lead_category: z.string(),
    lead_origin: z.string(),
    lead_source: z.string(),
    lead_sf_campaignid: z.string(),
    entry_url_path: z.string(),
  })
  .partial()
  .refine((input) => Object.values(input).some((v) => v !== undefined));

export type LeadSourceCookie = output<typeof leadSourceCookieSchema>;

export const getLeadSourceFromCookies = (cookies: Record<string, string>) => {
  const cookieValue = cookies[COOKIE_LEADSOURCE_ID];
  let cookieObject;

  try {
    cookieObject = JSON.parse(decodeURIComponent(cookieValue));
  } catch (_) {
    console.log('Failed to parse lead source cookie:', cookieValue);
    return null;
  }

  return leadSourceCookieSchema.parse(cookieObject);
};

const saveLeadSourceCookie = (
  cookies: EdgeFunctionsContext['cookies'],
  values: z.input<typeof leadSourceCookieSchema>
) => {
  const expires = new Date();
  const SIXTY_DAYS_MILLISECONDS = 60 * 24 * 60 * 60 * 1000;
  expires.setTime(expires.getTime() + SIXTY_DAYS_MILLISECONDS);
  const cookieValue = leadSourceCookieSchema.parse(values);

  cookies.set({
    name: COOKIE_LEADSOURCE_ID,
    value: encodeURIComponent(JSON.stringify(cookieValue)),
    expires,
    path: '/',
  });
};

export const getLeadSourceFromNetlify = (
  cookies: EdgeFunctionsContext['cookies'] | FunctionsContext['cookies']
) => {
  return getLeadSourceFromCookies({ [COOKIE_LEADSOURCE_ID]: cookies.get(COOKIE_LEADSOURCE_ID) });
};

// Helper to quickly create a type-safe and accurate lead source cookie object
export const createLeadSourceCookieValue = (values: z.input<typeof leadSourceCookieSchema>) => {
  return leadSourceCookieSchema.parse(values);
};

const getLeadSourceValuesFromURLParams = (searchParams: URLSearchParams) => {
  const campaignName = searchParams.get('utm_campaign') ?? undefined;
  const campaignContent = searchParams.get('utm_content') ?? undefined;
  const campaignTerm = searchParams.get('utm_term') ?? undefined;
  const lead_campaign_tag = searchParams.get('s_campaign') ?? undefined;
  const lead_content = searchParams.get('s_content') ?? undefined;
  const lead_term = searchParams.get('s_term') ?? undefined;
  const lead_category = searchParams.get('s_category') ?? 'Direct';
  const lead_source = searchParams.get('s_source') ?? 'Direct Web';
  const lead_origin = searchParams.get('s_origin') ?? undefined;
  const lead_gclid = searchParams.get('gclid') ?? undefined;
  const lead_marketplace = searchParams.get('serverId') ?? undefined;
  const lead_sf_campaignid = searchParams.get('sf_id') ?? undefined;

  if (lead_marketplace !== undefined) {
    return createLeadSourceCookieValue({
      lead_category: 'Organic',
      lead_source: 'Internal Referral',
      lead_origin: 'SQ Marketplace',
    });
  }

  return createLeadSourceCookieValue({
    lead_category,
    lead_source,
    lead_origin,
    lead_campaign_tag: lead_campaign_tag ?? campaignName,
    lead_gclid,
    lead_content: lead_content ?? campaignContent,
    lead_term: lead_term ?? campaignTerm,
    lead_sf_campaignid,
  });
};

const getLeadSourceValuesFromReferer = (referer: string) => {
  const trackedSearchEngines = [
    'google',
    'yahoo',
    'baidu',
    'bing',
    'duckduckgo',
    'ecosia',
    'yandex',
    'naver',
  ];

  const internalReferrals = [
    {
      name: 'docs.sonarsource.com/sonarqube',
      webSource: 'SQ Docs',
    },
    {
      name: 'docs.sonarsource.com/sonarlint',
      webSource: 'SL Docs',
    },
    {
      name: 'docs.sonarsource.com/sonarcloud',
      webSource: 'SC Docs',
    },
    {
      name: 'docs.sonarsource.com',
      webSource: 'Docs',
    },
    {
      name: 'rules.sonarsource',
      webSource: 'Rules',
    },
    {
      name: 'community.sonarsource',
      webSource: 'Community',
    },
  ];

  const socialMediaReferrals = [
    {
      name: 'twitter',
      webSource: 'Twitter/ X',
    },
    {
      name: 'linkedin',
      webSource: 'LinkedIn',
    },
    {
      name: 'youtube',
      webSource: 'YouTube',
    },
    {
      name: 'tiktok',
      webSource: 'TikTok',
    },
    {
      name: 'dev.to',
      webSource: 'DEV.to',
    },
    {
      name: 'instagram',
      webSource: 'Instagram',
    },
    {
      name: 'reddit',
      webSource: 'Reddit',
    },
  ];

  const externalReferrals = [
    {
      name: 'g2',
      webSource: 'G2',
    },
  ];

  const searchEngine = trackedSearchEngines.find((name) => referer.includes(name));

  if (searchEngine) {
    return createLeadSourceCookieValue({
      lead_category: 'Organic',
      lead_source: 'Search',
      lead_origin: searchEngine,
    });
  }

  const internalReferral = internalReferrals.find((ref) => referer.includes(ref.name));
  if (internalReferral) {
    return createLeadSourceCookieValue({
      lead_category: 'Organic',
      lead_source: 'Internal Referral',
      lead_origin: internalReferral.webSource,
    });
  }

  const socialMediaReferral = socialMediaReferrals.find((ref) => referer.includes(ref.name));
  if (socialMediaReferral) {
    return createLeadSourceCookieValue({
      lead_category: 'Organic',
      lead_source: 'Social media',
      lead_origin: socialMediaReferral.webSource,
    });
  }

  const externalReferral = externalReferrals.find((ref) => referer.includes(ref.name));
  if (externalReferral) {
    return createLeadSourceCookieValue({
      lead_category: 'Organic',
      lead_source: 'External Referral',
      lead_origin: externalReferral.webSource,
    });
  }
  return createLeadSourceCookieValue({
    lead_category: 'Organic',
    lead_source: 'External Referral',
    lead_origin: referer,
  });
};

export default async (request: Request, { cookies, geo, next }: EdgeFunctionsContext) => {
  /***** HANDLE LEAD COOKIES *****/

  const isNetlifyEnvironment = !!Netlify.context;
  const url = new URL(request.url);
  const referer = request.headers.get('referer') ?? '';

  const leadSourceCookie = getLeadSourceFromNetlify(cookies);
  const isSourceDirect = leadSourceCookie?.lead_source?.includes('Direct') || false;
  const isCookieBroken = [
    'Search',
    'Internal Referral',
    'Social media',
    'External Referral',
  ].includes(leadSourceCookie?.lead_origin || '');
  const hasUrlParams = url.searchParams.size > 0;
  const hasReferrer = referer !== '';

  try {
    if (isNetlifyEnvironment) {
      console.log('Request:', request);
      console.log('Request URL:', url);
      console.log('Lead source cookie:', leadSourceCookie);
    }
    // Create a new cookie if non existing, or if the existing one is from a "Direct" source
    if (isCookieBroken || !leadSourceCookie || isSourceDirect) {
      const entryUrlPath = url.pathname;
      const leadSourceValues =
        hasUrlParams || !hasReferrer
          ? getLeadSourceValuesFromURLParams(url.searchParams)
          : getLeadSourceValuesFromReferer(referer);
      const cookieValue = { ...leadSourceValues, entry_url_path: entryUrlPath };

      saveLeadSourceCookie(cookies, cookieValue);
    }
  } catch (error) {
    console.error(error);
  }

  /***** HANDLE GEOLOCATION *****/

  if (!geo.country?.name || !geo.country.code) {
    return;
  }

  const shouldHideMarketingOptOut = !GDPRCompliantCountries.includes(geo.country?.code);

  const currentResponse = await next();
  let responseBody = await currentResponse.text();
  responseBody = injectCountryDataIntoHtml(responseBody, geo.country?.name, geo.country?.code);

  if (shouldHideMarketingOptOut) {
    responseBody = hidemarketingOptOutInput(responseBody);
  }

  return new Response(responseBody, currentResponse);
};

function injectCountryDataIntoHtml(html: string, countryName?: string, countryCode?: string) {
  return html.replace(
    '<html ',
    `<html data-geo-country-name="${countryName}" data-geo-country-code="${countryCode}" `
  );
}

function hidemarketingOptOutInput(html: string) {
  return html.replace(
    '</head>',
    `<style>
      fieldset[name=${MARKETING_OPT_OUT_FIELD_ID}] {
        display: none;
      }
    </style>
    </head>`
  );
}

export const config: Config = {
  path: '/*',
  excludedPath: ['/*.*', '/api/*', COMPONENT_A_B_TEST_VARIANTS_PATH],
  onError: 'bypass',
};
